'use client';

import { useCallback, useState } from 'react';
import { cn } from '@/util/cn';
import type { IPlanYourTripProps } from '@/components/common/PlanYourTrip/types';
import { useOTSTranslation } from '@/i18n/useOTSTranslation';
import PartnerBlock from '@/components/resort/PartnerBlock/PartnerBlock';
import { useSanitizedHtmlProps } from '@/util/sanitized';
import DynamicSkiRentalForm from '@/components/common/PlanYourTrip/SkiRental/DynamicSkiRentalForm';
import { isSkiRentalsLocale } from '@/util/i18nFlags';
import { useNoHeader } from '@/util/useNoHeader';
import TripControlBlock from './TripControlBlock';
import WeSki from './WeSki/WeSkiWidget';
import { PlaningViewsEnum } from './constants';
import { getAvailableLinks, getAvailablePanels, getDefaultView } from './visibility';

const condensedClass = [
  '-mt-10',
  'max-lg:mt-0',
].join(' ');

// Matches the section H3 used on the Resort Overview template
// (Important Dates, etc.) so all in-body section headers carry the
// same weight and color.
const headingClass = 'mb-3 font-heading text-xl text-secondary md:text-2xl';

export const PlanYourTrip = ({
  title,
  tabDescription,
  initialTab,
  redirectSkiRentalPath,
  hasSkiRental,
  lodgingProvider,
  weSkiDestinationId,
  liftsUrl,
  rentalUrl,
  lessonsUrl,
  onSkiRentalChange,
  isPartner,
  contextKey,
  resortCountry,
  children,
  templateName,
  isCondensed = false,
  resortId,
  regionId,
}: IPlanYourTripProps) => {
  const { t } = useOTSTranslation('common');
  const { noHeader } = useNoHeader();

  const panels = getAvailablePanels({
    lodgingProvider,
    weSkiDestinationId,
    regionId,
    resortId,
    hasSkiRental,
    isPartner,
    initialTab,
    redirectSkiRentalPath,
    rentalUrl,
    resortCountry,
    templateName,
  });

  const links = getAvailableLinks({
    hasSkiRental,
    isPartner,
    initialTab,
    redirectSkiRentalPath,
    rentalUrl,
    resortCountry,
    liftsUrl,
    lessonsUrl,
    templateName,
  });

  const [activeView, setActiveView] = useState<PlaningViewsEnum>(getDefaultView(panels, initialTab));

  const onButtonClick = useCallback(
    (value: PlaningViewsEnum) => {
      setActiveView(value);
    },
    [activeView],
  );
  const htmlProps = useSanitizedHtmlProps(tabDescription || '');

  const getPartnerLink = useCallback(() => {
    if (activeView === PlaningViewsEnum.rental && rentalUrl) {
      return rentalUrl;
    }
    if (activeView === PlaningViewsEnum.lessons && lessonsUrl) {
      return lessonsUrl;
    }

    return undefined;
  }, [activeView, rentalUrl, lessonsUrl]);

  const showRentalAsForm = isSkiRentalsLocale() && hasSkiRental && !!redirectSkiRentalPath;

  // Link chips (lift access, ski lessons, an external rental link) are content
  // too: a resort with no WeSki mapping and no bookable rental panel can still
  // have all three, and hiding the widget over a missing panel used to drop
  // them on the floor. They only reach the screen through the tab row, though,
  // so they can't stand in for a panel where that row isn't rendered.
  const hasLinkRow = links.any && !isCondensed && !noHeader;

  if (!panels.lodging && !panels.rental && !hasLinkRow && !children) {
    return null;
  }

  return (
    <article className={cn('w-full', isCondensed && condensedClass)}>
      {!isCondensed && (
        <>
          <h3 className={headingClass}>{t('tripWidget.planYourTrip')}</h3>
          <TripControlBlock
            title={title}
            liftsUrl={liftsUrl}
            activeView={activeView}
            rentalUrl={rentalUrl}
            lessonsUrl={lessonsUrl}
            onButtonClick={onButtonClick}
            redirectSkiRentalPath={redirectSkiRentalPath}
            isPartner={isPartner}
            contextKey={contextKey}
            resortCountry={resortCountry}
            hasSkiRental={hasSkiRental}
            initialTab={initialTab || PlaningViewsEnum.lodging}
            lodgingProvider={lodgingProvider}
            templateName={templateName}
            resortId={resortId}
          />
        </>
      )}
      {activeView === PlaningViewsEnum.lodging && lodgingProvider === 'weski' && weSkiDestinationId && (
        <WeSki destinationId={weSkiDestinationId} />
      )}
      {
        showRentalAsForm && activeView === PlaningViewsEnum.rental && (
          <DynamicSkiRentalForm
            searchRedirectUrl={redirectSkiRentalPath}
            onChange={onSkiRentalChange}
          />
        )
      }
      {
        !showRentalAsForm && activeView === PlaningViewsEnum.rental && (
          <PartnerBlock
            partnerLinkUrl={getPartnerLink()}
            partnerType={activeView}
            title={title}
            contextKey="resort"
          />
        )
      }
      {
        activeView === PlaningViewsEnum.lessons && (
          <PartnerBlock
            partnerLinkUrl={getPartnerLink()}
            partnerType={activeView}
            title={title}
            contextKey="resort"
          />
        )
      }
      {
        activeView === PlaningViewsEnum.flights && tabDescription && (
          <div className="px-3">
            <div {...htmlProps} />
          </div>
        )
      }
      {children}
    </article>
  );
};
